<template>
  <div class="import-db">
    <div class="header">
      <div class="title">
        <div class="name">{{ packagedName }}</div>
        <div class="meta">
          {{ objectsCount }} object{{ objectsCount === 1 ? "" : "s" }}
        </div>
      </div>

      <div class="status">
        <span v-if="effectiveMode === 'create'" class="pill">to create</span>
        <span v-else-if="effectiveMode === 'extend'" class="pill"
          >extend database</span
        >
        <span v-else class="pill">use database</span>
        <span v-if="effectiveMode !== 'create' && idExists" class="pill ok"
          >ID exists</span
        >
      </div>
    </div>

    <div class="mapping">
      <div class="field-row">
        <div class="field-label">Choose destination</div>
        <BaseInputSelect
          :value="mode === 'create' ? 'create' : 'use'"
          :options="destinationOptions"
          :debounce-ms="0"
          select-only
          @selected="(opt) => setMode(opt.value as Mode)"
        />
      </div>
      <div v-if="mode !== 'create'" class="field-row">
        <div class="field-label">Choose database</div>
        <BaseInputSelect
          :value="selectedTargetId ?? ''"
          :options="databaseOptions"
          :disabled="!workspaceDbDict || databaseOptions.length === 0"
          :debounce-ms="0"
          select-only
          searchable
          placeholder="Select database"
          @update:value="(v) => onTargetPicked(v as TableId)"
        />
      </div>
    </div>

    <div class="diff" v-if="showProperties">
      <div class="diff-title">Properties</div>
      <div v-if="properties.length === 0" class="empty">
        No properties found in schema.
      </div>
      <div v-else class="props">
        <div
          v-for="p in properties"
          :key="p.id"
          class="prop"
          :class="{ add: p.willAdd }"
        >
          <span class="prop-name">{{ p.name }}</span>
          <span class="prop-type">
            {{
              p.workspaceValueType || p.packagedValueType || p.valueType || "—"
            }}
          </span>
          <div class="prop-status">
            <span v-if="p.isConflict" class="conflict">
              Conflict ({{
                p.packagedValueType ||
                p.expectedType ||
                p.valueType ||
                "unknown"
              }})
            </span>
            <span v-if="p.willAdd" class="tag">New</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";

import type { GraphDatabaseDictionary, TableId } from "@ctrl/controller-rpc";

import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import { isDefaultExtensionTableId } from "@/components/databases";
import type { InstallDatabaseMode } from "@/components/marketplace/import";
import type { PackagedDatabase } from "@/components/marketplace/types";
import { typeDiff } from "@/components/marketplace/utils";
import { isJsonRecord } from "@/utils/json";

type Mode = InstallDatabaseMode;
type EffectiveMode = "create" | "use" | "extend";

export default defineComponent({
  name: "ImportDatabase",
  components: { BaseInputSelect },
  props: {
    packagedDbId: { type: String, required: true },
    packagedDb: { type: Object as PropType<PackagedDatabase>, required: true },
    objectsCount: { type: Number, required: true },
    workspaceDbDict: {
      type: Object as PropType<GraphDatabaseDictionary | undefined>,
      required: false,
    },
    selectedTargetId: {
      type: String as PropType<TableId | undefined>,
      required: false,
    },
    hiddenDatabaseIds: {
      type: Array as PropType<TableId[]>,
      required: false,
      default: () => [],
    },
    mode: { type: String as PropType<Mode>, required: true },
  },
  emits: {
    "update:mode": (_v: Mode) => true,
    "update:selectedTargetId": (_v: TableId | undefined) => true,
  },
  setup(props, { emit }) {
    // A packaged id that matches a default-type extension table must not be
    // treated as an implicit destination: those tables are not importable.
    const idExists = computed(
      () =>
        Boolean(props.workspaceDbDict?.[props.packagedDbId]) &&
        !isDefaultExtensionTableId(props.packagedDbId)
    );

    const packagedName = computed(() => {
      const schemaRec = isJsonRecord(props.packagedDb.schema)
        ? props.packagedDb.schema
        : null;
      const dn = schemaRec ? schemaRec["data_name"] : null;
      const n = schemaRec ? schemaRec["name"] : null;
      return (
        (typeof dn === "string" && dn) ||
        (typeof n === "string" && n) ||
        props.packagedDbId ||
        "Database"
      );
    });

    const effectiveWorkspaceDb = computed(() => {
      if (props.mode === "create") return null;
      if (idExists.value)
        return props.workspaceDbDict?.[props.packagedDbId] ?? null;
      const id = props.selectedTargetId;
      if (!id) return null;
      return props.workspaceDbDict?.[id] ?? null;
    });

    const showProperties = computed(
      () => props.mode !== "create" && Boolean(effectiveWorkspaceDb.value)
    );

    const properties = computed(() =>
      typeDiff({
        packagedSchema: props.packagedDb.schema,
        workspaceSchema: effectiveWorkspaceDb.value,
      })
    );

    const effectiveMode = computed<EffectiveMode>(() => {
      if (props.mode === "create") return "create";
      if (props.mode === "extend") return "extend";
      if (!effectiveWorkspaceDb.value) return "use";
      return properties.value.some((p) => p.willAdd) ? "extend" : "use";
    });

    const databaseOptions = computed<SelectOption[]>(() => {
      const dict = props.workspaceDbDict ?? {};
      const hidden = new Set(props.hiddenDatabaseIds ?? []);
      const selected = props.selectedTargetId;
      return Object.values(dict)
        .filter(
          (db) =>
            (db.id === selected || !hidden.has(db.id)) &&
            db.permissions?.can_create_row !== false &&
            // Default types (Task/Event/Page/Person…) cannot be import
            // destinations: their rows are managed by dedicated RPCs and
            // extension rows, not by the generic row import.
            !isDefaultExtensionTableId(String(db.id))
        )
        .sort((a, b) =>
          (a.data_name || a.name || "").localeCompare(
            b.data_name || b.name || ""
          )
        )
        .map((db) => {
          const emoji = db.meta?.emoji;
          const name = db.data_name || db.name || String(db.id);
          return {
            value: String(db.id),
            label: emoji ? `${emoji} ${name}` : name,
            aliases: [db.data_name, db.name].filter(
              (s): s is string => typeof s === "string" && s.length > 0
            ),
          };
        });
    });

    const destinationOptions: SelectOption[] = [
      { value: "create", label: "Create new database", aliases: [] },
      { value: "use", label: "Use existing database", aliases: [] },
    ];

    const setMode = (next: Mode) => {
      emit("update:mode", next);
      if (next === "create") emit("update:selectedTargetId", undefined);
    };

    const onTargetPicked = (id: TableId | undefined) => {
      emit("update:selectedTargetId", id);
    };

    return {
      databaseOptions,
      destinationOptions,
      effectiveMode,
      idExists,
      onTargetPicked,
      packagedName,
      properties,
      setMode,
      showProperties,
    };
  },
});
</script>

<style scoped lang="scss">
.import-db {
  border-radius: $border-radius-m;
  padding: 12px;
  @include themed() {
    border: 1px solid t($grey-20);
    background: t($bg);
  }

  .header {
    display: flex;
    justify-content: space-between;
    column-gap: 12px;
    align-items: flex-start;
  }

  .title {
    display: flex;
    flex-direction: column;
    row-gap: 4px;
  }

  .name {
    @extend %font-body-medium;
    @include themed() {
      color: t($grey-90);
    }
  }

  .meta {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-60);
    }
  }

  .pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    @extend %font-caption-medium;
    @include themed() {
      color: t($grey-80);
      background: t($grey-05);
      border: 1px solid t($grey-20);
    }
  }

  .pill.ok {
    @include themed() {
      color: t($grey-90);
      background: t($grey-03);
    }
  }

  .mapping {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  }

  .field-row {
    display: grid;
    grid-template-columns: 130px 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .field-label {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-60);
    }
  }

  .diff {
    margin-top: 12px;
  }

  .diff-title {
    @extend %font-caption-medium;
    margin-bottom: 6px;
    @include themed() {
      color: t($grey-60);
    }
  }

  .empty {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-60);
    }
  }

  .props {
    display: flex;
    flex-direction: column;
    row-gap: 6px;
  }

  .prop {
    display: grid;
    grid-template-columns: 140px 160px 1fr;
    align-items: baseline;
    column-gap: 12px;
    padding: 6px 8px;
    border-radius: $border-radius-s;
    @include themed() {
      background: t($bg-alt);
      border: 1px solid t($grey-15);
    }
  }

  .prop.add {
    @include themed() {
      border-color: rgba($accepted, 0.4);
    }
  }

  .prop-name {
    @extend %font-caption-regular;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @include themed() {
      color: t($grey-90);
    }
  }

  .prop-type {
    @extend %font-caption-regular;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @include themed() {
      color: t($grey-60);
    }
  }

  .prop-status {
    display: flex;
    align-items: center;
    column-gap: 6px;
    min-width: 0;
  }

  .conflict {
    @extend %font-caption-medium;
    border-radius: 999px;
    padding: 2px 8px;
    white-space: nowrap;
    color: $declined;
    background: rgba($declined, 0.08);
    border: 1px solid rgba($declined, 0.25);
  }

  .tag {
    @extend %font-caption-medium;
    flex-shrink: 0;
    padding: 1px 6px;
    color: $accepted;
    border-radius: $border-radius-m;
    @include themed() {
      background: rgba($accepted, 0.08);
    }
  }
}
</style>
